import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import { Hourhand, HourhandResponse } from '../interfaces/hourhands.interfaces';
import { AccessState } from '../../../../types';

export const hourhandsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Hourhands', 'PostgraduatePlanningHourhands'],
});

// Define a service using a base URL and expected endpoints
export const hourhandsApi = hourhandsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all hourhands
     */
    getHourhands: builder.query<
      HourhandResponse,
      {
        profileId?: number;
        page: number;
        perPage: number;
        search: string;
        sort: string;
        type_sort: string;
      }
    >({
      queryFn: async (
        { profileId, page, search, perPage, sort, type_sort },
        { dispatch, getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<HourhandResponse>('/hourhands', {
            params: {
              user_profile_id,
              page,
              size: perPage,
              type_sort: type_sort || 'desc',
              ...(sort && { sort }),
              search,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Hourhands', id } as const)),
              { type: 'Hourhands', id: 'LIST' },
            ]
          : [{ type: 'Hourhands', id: 'LIST' }],
    }),

    getAllHourhands: builder.query<
      Hourhand[],
      {
        profileId: number;
      }
    >({
      queryFn: async ({ profileId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<Hourhand[]>('/hourhands', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'Hourhands', id: 'LIST' }],
    }),

    /**
     * @GET all hourhand availables
     */
    getAvailablesHourhand: builder.query<Hourhand[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<Hourhand[]>('/hourhands', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Hourhands', profileId }],
    }),

    /**
     * @GET hourhand by id
     */
    getHourhand: builder.query<Hourhand, { profileId?: number; hourhandId: number }>({
      queryFn: async ({ profileId, hourhandId }, { dispatch, getState }) => {
        const {
          access: { currentProfile },
        } = getState() as { access: AccessState };

        const user_profile_id = profileId || currentProfile?.profileId;
        try {
          const { data } = await mainApi.get<Hourhand>(`/hourhands/${hourhandId}`, {
            params: {
              user_profile_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'Hourhands', id: result?.id }],
    }),

    /**
     * @POST create hourhand
     */
    addHourhand: builder.mutation<
      Hourhand,
      { profileId: number; hourhandPayload: Partial<Hourhand> }
    >({
      queryFn: async ({ profileId, hourhandPayload }) => {
        try {
          const { data } = await mainApi.post<Hourhand>('/hourhands', {
            user_profile_id: profileId,
            ...hourhandPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'Hourhands', id: 'LIST' },
        {
          type: 'PostgraduatePlanningHourhands',
          id: 'TABLE',
        },
      ],
    }),

    /**
     * @PUT update hourhand
     */
    updateHourhand: builder.mutation<
      Hourhand,
      { profileId: number; hourhandId: number; hourhandPayload: Partial<Hourhand> }
    >({
      queryFn: async ({ profileId, hourhandId, hourhandPayload }) => {
        try {
          const { data } = await mainApi.put<Hourhand>(`/hourhands/${hourhandId}`, {
            user_profile_id: profileId,
            ...hourhandPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result) =>
        result?.id
          ? [
              { type: 'Hourhands', id: result?.id },
              { type: 'Hourhands', id: 'LIST' },
              {
                type: 'PostgraduatePlanningHourhands',
                id: 'TABLE',
              },
            ]
          : [],
    }),

    /**
     * @DELETE remove hourhand
     */
    deleteHourhand: builder.mutation<Hourhand, { profileId: number; hourhandId: number }>({
      queryFn: async ({ profileId, hourhandId }) => {
        try {
          const { data } = await mainApi.delete(`hourhands/${hourhandId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result) => [
        { type: 'Hourhands', id: result?.id },
        { type: 'Hourhands', id: 'LIST' },
        {
          type: 'PostgraduatePlanningHourhands',
          id: 'TABLE',
        },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetHourhandsQuery,
  useLazyGetHourhandQuery,
  useGetHourhandQuery,
  useAddHourhandMutation,
  useDeleteHourhandMutation,
  useUpdateHourhandMutation,
  useGetAvailablesHourhandQuery,
  useLazyGetAllHourhandsQuery,
} = hourhandsApi;
